import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";
import ExecutionProgress from "./ExecutionProgress";

// ── Debounce helper ──────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Read-only field ──────────────────────────────────────────────────────────
function ReadOnlyField({ label, value, placeholder = "—" }) {
  return (
    <div className="form-field">
      <label className="field-label">{label}</label>
      <div className="field-readonly">{value || placeholder}</div>
    </div>
  );
}

// ── User search dropdown ─────────────────────────────────────────────────────
function UserSearchField({ label, value, onChange, onSelect, placeholder, token }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const wrapperRef = useRef(null);
  const debouncedQuery = useDebounce(value, 350);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 3 || !token) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API_BASE_URL}/offboarding/searchUsers`, {
        params: { query: debouncedQuery },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) {
          setResults(res.data.users || []);
          setOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Search failed. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedQuery, token]);

  const handleSelect = (user) => {
    onSelect(user);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="form-field" ref={wrapperRef}>
      <label className="field-label">{label}</label>
      <div className="search-wrapper">
        <input
          type="text"
          className="field-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
        />
        {loading && <span className="search-spinner" />}
      </div>
      {error && <p className="field-error">{error}</p>}
      {open && results.length > 0 && (
        <ul className="search-dropdown">
          {results.map((u) => (
            <li
              key={u.id}
              className="search-result"
              onMouseDown={() => handleSelect(u)}
            >
              <span className="result-name">{u.displayName}</span>
              <span className="result-upn">{u.userPrincipalName}</span>
            </li>
          ))}
        </ul>
      )}
      {open && results.length === 0 && !loading && (
        <ul className="search-dropdown">
          <li className="search-result search-no-results">No users found</li>
        </ul>
      )}
    </div>
  );
}

// ── Main form ────────────────────────────────────────────────────────────────
export default function OffboardingForm({ onSubmitted, onViewHistory }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const submittedBy = user.username || "";

  const [token, setToken] = useState(null);

  // Employee fields
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Delegate fields
  const [delegateQuery, setDelegateQuery] = useState("");
  const [delegates, setDelegates] = useState([]); // array of { displayName, userPrincipalName }

  // LastDay
  const [lastDay, setLastDay] = useState("");

  // Form state
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState(null); // "record" | "executing"
  const [submitError, setSubmitError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Execution progress overlay
  const [showProgress, setShowProgress] = useState(false);
  const [progressLog, setProgressLog] = useState("");
  const [progressRunning, setProgressRunning] = useState(false);
  const [progressTransaction, setProgressTransaction] = useState("");
  const [progressEmployee, setProgressEmployee] = useState("");
  const pollIntervalRef = useRef(null);

  // Acquire token on mount
  useEffect(() => {
    getApiToken(instance).then(setToken);
  }, [instance]);

  // Fetch full user details (including manager) when an employee is selected
  const fetchEmployeeDetails = useCallback(
    async (upn) => {
      if (!token) return;
      setLoadingDetails(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/offboarding/getUserDetails`, {
          params: { upn },
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployeeDetails(res.data.user);
      } catch {
        setEmployeeDetails(null);
      } finally {
        setLoadingDetails(false);
      }
    },
    [token]
  );

  const handleEmployeeSelect = (u) => {
    setSelectedEmployee(u);
    setEmployeeQuery(u.displayName);
    setEmployeeDetails(null);
    fetchEmployeeDetails(u.userPrincipalName);
    setValidationErrors((prev) => ({ ...prev, employee: undefined }));
  };

  const handleEmployeeQueryChange = (val) => {
    setEmployeeQuery(val);
    if (selectedEmployee) {
      // User is typing again — clear the selection
      setSelectedEmployee(null);
      setEmployeeDetails(null);
    }
  };

  const handleDelegateSelect = (u) => {
    setDelegateQuery("");
    // Prevent duplicates
    if (delegates.some((d) => d.userPrincipalName === u.userPrincipalName)) return;
    // Prevent adding the employee being offboarded as a delegate
    if (
      selectedEmployee &&
      u.userPrincipalName.toLowerCase() ===
        selectedEmployee.userPrincipalName.toLowerCase()
    )
      return;
    setDelegates((prev) => [...prev, { displayName: u.displayName, userPrincipalName: u.userPrincipalName }]);
  };

  const removeDelegate = (upn) => {
    setDelegates((prev) => prev.filter((d) => d.userPrincipalName !== upn));
  };

  // Validation
  const validate = () => {
    const errors = {};
    if (!selectedEmployee || !employeeDetails) {
      errors.employee = "Please search for and select the employee being offboarded.";
    }
    if (!lastDay) {
      errors.lastDay = "Please select the employee's last day.";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setSubmitting(true);
    setSubmitStep("record");

    try {
      const delegateUPNs = delegates.map((d) => d.userPrincipalName).join(",");
      const payload = {
        displayName: employeeDetails.displayName,
        userPrincipalName: employeeDetails.userPrincipalName,
        jobTitle: employeeDetails.jobTitle || "",
        department: employeeDetails.department || "",
        manager: employeeDetails.manager || "",
        submittedBy,
        lastDay,
        delegates: delegateUPNs,
      };

      // Step 1 — create SharePoint record
      const res = await axios.post(
        `${API_BASE_URL}/offboarding/submit`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const { transactionNumber, dateSubmitted, itemId } = res.data;

      // Show progress overlay and start polling SP for live log updates
      setProgressTransaction(transactionNumber);
      setProgressEmployee(payload.displayName);
      setProgressLog("");
      setProgressRunning(true);
      setShowProgress(true);
      setSubmitStep("executing");

      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/offboarding/status`, {
            params: { itemId },
            headers: { Authorization: `Bearer ${token}` },
          });
          setProgressLog(statusRes.data.actionLog || "");
        } catch {
          // ignore polling errors silently
        }
      }, 2000);

      // Step 2 — run automation (long-running)
      const execRes = await axios.post(
        `${API_BASE_URL}/offboarding/execute`,
        { itemId, transactionNumber, userPrincipalName: payload.userPrincipalName, delegates: delegateUPNs, submittedBy },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      // Stop polling and apply final log
      clearInterval(pollIntervalRef.current);
      setProgressLog(execRes.data.actionLog || "");
      setProgressRunning(false);

      // Brief pause to show completed state before navigating
      await new Promise((r) => setTimeout(r, 2000));

      onSubmitted({
        ...payload,
        transactionNumber,
        dateSubmitted,
        itemId,
        executionStatus: execRes.data.status,
        actionLog: execRes.data.actionLog,
      });
    } catch (err) {
      clearInterval(pollIntervalRef.current);
      setShowProgress(false);
      setProgressRunning(false);
      const msg =
        err.response?.data?.error || "Submission failed. Please try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
      setSubmitStep(null);
    }
  };

  const displayedEmployee = employeeDetails || selectedEmployee;

  return (
    <>
      {showProgress && (
        <ExecutionProgress
          transactionNumber={progressTransaction}
          employeeName={progressEmployee}
          actionLog={progressLog}
          isRunning={progressRunning}
        />
      )}
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Offboarding</span>
          </div>
          <div className="header-actions">
            <button className="btn-header-ghost" onClick={onViewHistory}>
              View History
            </button>
            <span className="header-username">{user.name || submittedBy}</span>
          </div>
        </div>
      </header>

      <main className="form-container">
        <div className="form-card">
          <div className="form-card-header">
            <h2>Offboarding Request</h2>
            <p>
              Complete the form below to initiate the offboarding process. All
              fields marked as required must be filled before submission.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* ── Section: Submitted By ── */}
            <section className="form-section">
              <h3 className="section-title">Requester</h3>
              <ReadOnlyField label="Submitted By" value={submittedBy} />
            </section>

            {/* ── Section: Employee ── */}
            <section className="form-section">
              <h3 className="section-title">
                Employee Being Offboarded <span className="required-star">*</span>
              </h3>

              <UserSearchField
                label="Search by name or email"
                value={employeeQuery}
                onChange={handleEmployeeQueryChange}
                onSelect={handleEmployeeSelect}
                placeholder="Type at least 3 characters..."
                token={token}
              />
              {validationErrors.employee && (
                <p className="field-error">{validationErrors.employee}</p>
              )}

              {loadingDetails && (
                <p className="loading-hint">Loading employee details...</p>
              )}

              {displayedEmployee && !loadingDetails && (
                <div className="auto-populated-fields">
                  <div className="fields-grid">
                    <ReadOnlyField
                      label="Display Name"
                      value={displayedEmployee.displayName}
                    />
                    <ReadOnlyField
                      label="User Principal Name"
                      value={displayedEmployee.userPrincipalName}
                    />
                    <ReadOnlyField
                      label="Job Title"
                      value={employeeDetails?.jobTitle}
                      placeholder="Not set"
                    />
                    <ReadOnlyField
                      label="Department"
                      value={employeeDetails?.department}
                      placeholder="Not set"
                    />
                    <ReadOnlyField
                      label="Manager"
                      value={employeeDetails?.manager}
                      placeholder="Not set"
                    />
                  </div>
                </div>
              )}
            </section>

            {/* ── Section: Last Day ── */}
            <section className="form-section">
              <h3 className="section-title">
                Last Day <span className="required-star">*</span>
              </h3>
              <div className="form-field">
                <label className="field-label">Select last working day</label>
                <input
                  type="date"
                  className={`field-input field-date ${
                    validationErrors.lastDay ? "field-input-error" : ""
                  }`}
                  value={lastDay}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => {
                    setLastDay(e.target.value);
                    setValidationErrors((prev) => ({ ...prev, lastDay: undefined }));
                  }}
                />
                {validationErrors.lastDay && (
                  <p className="field-error">{validationErrors.lastDay}</p>
                )}
              </div>
            </section>

            {/* ── Section: Delegates ── */}
            <section className="form-section">
              <h3 className="section-title">Delegates (Optional)</h3>
              <p className="section-description">
                Search for users who should receive delegated access on behalf
                of the departing employee. Only enabled accounts are shown.
              </p>

              <UserSearchField
                label="Search for a delegate"
                value={delegateQuery}
                onChange={setDelegateQuery}
                onSelect={handleDelegateSelect}
                placeholder="Type at least 3 characters..."
                token={token}
              />

              {delegates.length > 0 && (
                <div className="delegates-list">
                  {delegates.map((d) => (
                    <div key={d.userPrincipalName} className="delegate-chip">
                      <span className="chip-name">{d.displayName}</span>
                      <span className="chip-upn">{d.userPrincipalName}</span>
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={() => removeDelegate(d.userPrincipalName)}
                        aria-label={`Remove ${d.displayName}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Submit ── */}
            {submitError && (
              <div className="submit-error">
                <strong>Error:</strong> {submitError}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary btn-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner" />
                    {submitStep === "executing" ? "Running automation..." : "Creating record..."}
                  </>
                ) : (
                  "Submit Offboarding Request"
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
    </>
  );
}
